import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

export default function AshaStory() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="asha"
      ref={ref}
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: "var(--cream)" }}
      data-testid="asha-section"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="relative w-full aspect-[4/5] rounded-[28px] overflow-hidden">
              <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_doc-marketplace-6/artifacts/e0ab088f779c71ea_ChatGPT%20Image%20Sep%2029%2C%202026%2C%2004_57_40%20PM-7.png"
                  alt="East African dairy entrepreneur recording sales in her shop surrounded by milk and yogurt"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div
                className="absolute inset-0 pointer-events-none mix-blend-multiply"
                style={{
                  background:
                    "radial-gradient(60% 55% at 65% 35%, transparent 0%, rgba(15,31,23,0.35) 90%)",
                }}
              />
              <div className="grain absolute inset-0 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[var(--cream)]">
                <div className="font-mono-caption text-[10px] tracking-[0.25em] uppercase bg-[rgba(15,31,23,0.55)] backdrop-blur-sm rounded-full px-3 py-1.5">
                  {t.asha.caption}
                </div>
                <div className="w-9 h-9 rounded-full bg-[var(--marigold)] flex items-center justify-center">
                  <span className="text-[var(--ink)] font-display text-lg">A</span>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden md:block absolute -right-6 -bottom-6 bg-[var(--ink)] text-[var(--cream)] rounded-2xl p-5 max-w-[240px] shadow-2xl"
            >
              <div className="font-display text-4xl leading-none">{t.asha.stat_head}</div>
              <div className="mt-2 text-[13px] text-[var(--cream)]/70 leading-snug">
                {t.asha.stat_desc}
              </div>
            </motion.div>
          </div>

          <div>
            <div className="chip-num mb-4">{t.asha.chip}</div>
            <h2 className="font-display font-medium text-[2.4rem] sm:text-[3rem] lg:text-[3.8rem] leading-[1] tracking-[-0.03em] text-[var(--ink)]">
              {t.asha.h2_a}{" "}
              <span className="font-editorial-italic">{t.asha.h2_italic}</span>{" "}
              {t.asha.h2_b}
            </h2>

            <div className="mt-8 space-y-5 text-[var(--ink-2)]/85 text-base md:text-[17px] leading-[1.7] max-w-[54ch]">
              <p>
                {t.asha.p1_a}{" "}
                <span className="underline-marigold">{t.asha.p1_link}</span>{" "}
                {t.asha.p1_b}
              </p>
              <p>
                {t.asha.p3_a}{" "}
                <span className="font-editorial-italic text-[var(--marigold)]">
                  {t.asha.p3_italic}
                </span>
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              {t.asha.chips.map((s, i) => (
                <div
                  key={i}
                  className="bg-[var(--cream-2)] rounded-2xl px-5 py-4 border border-[rgba(15,31,23,0.06)]"
                >
                  <div className="font-display text-2xl text-[var(--ink)] leading-none">{s.k}</div>
                  <div className="mt-1 text-[12px] text-[var(--muted)] max-w-[160px] leading-snug">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
